import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link as RouterLink, useLocation } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Heading,
  Text,
  Alert,
  AlertIcon,
  InputGroup,
  InputRightElement,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";

const LoginForm = () => {
  const [usernameOrEmail, setUsernameOrEmail] = useState("");
  const [password, setPassword] = useState("");
  const [localError, setLocalError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { login, error: authError, clearAuthError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const toastIdRef = useRef<string | number | undefined>();
  const hasShownErrorRef = useRef(false);

  const formBg = useColorModeValue("var(--color-card-bg)", "var(--color-card-bg)");
  const borderColor = useColorModeValue("var(--color-border)", "var(--color-border)");
  const subtitleColor = useColorModeValue("var(--color-text-muted)", "var(--color-text-muted)");

  useEffect(() => {
    // Show success toast if redirected after password reset
    const params = new URLSearchParams(location.search);
    if (params.get("reset") === "success") {
      toast({
        title: "Password reset successfully",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    }
  }, [location.search, toast]);

  useEffect(() => {
    if (authError && !hasShownErrorRef.current) {
      // Mark that we've shown this error
      hasShownErrorRef.current = true;
      
      // Close any existing toast to prevent duplicates
      if (toastIdRef.current) {
        toast.close(toastIdRef.current);
      }
      
      // Show new toast and store its ID
      toastIdRef.current = toast({
        id: `login-error-${Date.now()}`, // Unique ID prevents duplicates
        title: "Login Failed",
        description: authError,
        status: "error",
        duration: 5000,
        isClosable: true,
        onCloseComplete: () => {
          clearAuthError();
          hasShownErrorRef.current = false;
        },
      });
    } else if (!authError && hasShownErrorRef.current) {
      // Reset the flag when error is cleared
      hasShownErrorRef.current = false;
    }
  }, [authError, toast, clearAuthError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError("");
    
    // Clear any existing auth errors before attempting login
    clearAuthError();

    setIsLoading(true);

    const loginResult = await login(usernameOrEmail, password);
    if (loginResult.success) {
      if (loginResult.profileExists === true) {
        navigate("/chat");
      }
      // If loginResult.profileExists is false, AuthContext has already handled navigation.
    }
    // If login failed, the error will be set in AuthContext and caught by useEffect
    
    setIsLoading(false);
  };

  return (
    <VStack
      as="form"
      onSubmit={handleSubmit}
      w="full"
      maxW="md"
      mx="auto"
      mt={8}
      p={6}
      borderWidth="1px"
      borderRadius="lg"
      borderColor={borderColor}
      boxShadow="lg"
      bg={formBg}
      spacing={6}
    >
      <Heading size="lg">Welcome Back</Heading>
      <Text fontSize="sm" color={subtitleColor}>
        Please sign in to your account
      </Text>

      {localError && (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {localError}
        </Alert>
      )}

      <FormControl id="usernameOrEmail" isRequired>
        <FormLabel>Username or Email</FormLabel>
        <Input
          type="text"
          value={usernameOrEmail}
          onChange={(e) => setUsernameOrEmail(e.target.value)}
          placeholder="Enter your username or email"
          size="md"
          focusBorderColor="blue.500"
        />
      </FormControl>

      <FormControl id="password" isRequired>
        <FormLabel>Password</FormLabel>
        <InputGroup>
          <Input
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter your password"
            size="md"
            focusBorderColor="blue.500"
          />
          <InputRightElement width="4.5rem">
            <Button
              type="button"
              h="1.75rem"
              size="sm"
              onClick={() => setShowPassword(!showPassword)}
              variant="ghost"
            >
              {showPassword ? <ViewOffIcon /> : <ViewIcon />}
            </Button>
          </InputRightElement>
        </InputGroup>
      </FormControl>

      <Button
        type="submit"
        colorScheme="blue"
        size="md"
        width="full"
        isLoading={isLoading}
        loadingText="Logging in..."
      >
        Log In
      </Button>

      <Text fontSize="sm" textAlign="center">
        <RouterLink to="/forgot-password" style={{ color: "var(--color-primary)" }}>
          Forgot your password?
        </RouterLink>
      </Text>

      <Text fontSize="sm">
        Don't have an account?{" "}
        <RouterLink to="/signup" style={{ color: "var(--color-primary)" }}>
          Sign up
        </RouterLink>
      </Text>
    </VStack>
  );
};

export default LoginForm;
